"use client";

import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormMessage,
} from "@/shared/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { X } from "lucide-react";
import { BaseFieldProps, Option } from "./form-field-utils";
import { FormFieldLabel } from "./form-field-label";

export function FieldSelect({
    name,
    label,
    description,
    className,
    labelType = "text",
    tooltipContent,
    options = [],
    placeholder,
    disabled,
    clearable = true,
}: BaseFieldProps & {
    options?: Option[];
    placeholder?: string;
    disabled?: boolean;
    clearable?: boolean;
}) {
    return (
        <FormField
            name={name}
            render={({ field }) => (
                <FormItem className={className}>
                    <FormFieldLabel label={label} labelType={labelType} tooltipContent={tooltipContent} />
                    <FormControl>
                        <div className="relative">
                            <Select
                                value={field.value ?? ""}
                                // Radix'in gizli native select'i, değer mount'tan SONRA gelince
                                // (API'den çözülen alanlar) henüz kayıtlı option olmadığı için
                                // boş string'i forma geri yazıyor ve seçimi siliyor. Kullanıcı
                                // etkileşimi hiçbir zaman "" üretmez (boş item değeri yasak),
                                // temizleme aşağıdaki X butonundan yapılır — bu yüzden yok say.
                                onValueChange={(next) => { if (next !== "") field.onChange(next) }}
                                disabled={disabled}
                            >
                                <SelectTrigger ref={field.ref} className="w-full" disabled={disabled}>
                                    <SelectValue placeholder={placeholder} />
                                </SelectTrigger>
                                <SelectContent>
                                    {options.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                                            {opt.description ? (
                                                <span className="flex flex-col gap-0.5">
                                                    <span>{opt.label}</span>
                                                    {/* Disabled item tıklanamadığı için `title` hover'da açılmaz;
                                                        neden satır olarak yazılmazsa kullanıcı sebebini hiç görmez. */}
                                                    <span className="max-w-72 text-xs whitespace-normal text-muted-foreground">
                                                        {opt.description}
                                                    </span>
                                                </span>
                                            ) : (
                                                opt.label
                                            )}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {clearable && field.value && !disabled && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        field.onChange("");
                                    }}
                                    className="absolute right-8 top-1/2 -translate-y-1/2 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </FormControl>
                    {description && <FormDescription>{description}</FormDescription>}
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}
